import { useTranslation } from 'react-i18next';

import type { PitchSeries } from '@muzly/voice-engine';

import { breathCurve, breathPoints, linePath } from './chartGeometry';

/**
 * 호흡 안정도 라인 차트 (시안 §8.7 `1000:8628`) — 눈금 100·80·60·40·20, 레드 선 + 연분홍
 * 면적, 구간 가운데에 점, 아래 `0초 · 5초 · 10초`.
 *
 * 구간 점수 셋을 시간축에 놓는다. 막대가 아니라 선인 이유: 세 값이 **한 테이크 안에서**
 * 흐른 이야기라(잘 시작했다 무너졌나, 흔들리다 잡혔나) 이어 놓아야 그 모양이 보인다.
 *
 * 그 회차의 음정 곡선(`series`)이 있으면 선은 세 점을 잇는 대신 **실측 곡선**이다 — 1초 창
 * 이동 안정도(`breathCurve`, TestFlight #56). 구간 점은 그 위에 그대로 남는다. 곡선이 없는
 * 회차(서버 복원·이 필드 이전)는 예전처럼 세 점을 잇는다.
 *
 * 값이 없는 구간은 점을 안 찍는다 — 테이크가 거기까지 안 갔다는 뜻이고, 0으로 찍으면
 * 화면이 "그 구간에서 무너졌다"고 말한다. 점이 하나도 없으면 차트 자체를 안 그린다.
 *
 * 시안의 `평균 기준선`(점선)은 **비교할 평균이 있을 때만** 선다(`average`).
 */

const W = 339;
const H = 190;
const PAD = { l: 40, r: 16, t: 12, b: 30 };
const Y_TICKS = [100, 80, 60, 40, 20];

const yOf = (viewY: number): number =>
  PAD.t + (viewY / 100) * (H - PAD.t - PAD.b);
const xOf = (viewX: number): number =>
  PAD.l + (viewX / 100) * (W - PAD.l - PAD.r);

export const BreathLineChart = ({
  segments,
  scores,
  series,
  average,
}: {
  segments: readonly { id: string; fromSec: number; toSec: number }[];
  scores: readonly { id: string; score: number }[];
  /** 그 회차의 음정 곡선. 있으면 선이 실측 곡선이 된다. */
  series?: PitchSeries;
  /** 0..100 기준선. 없으면 안 그린다. */
  average?: number;
}) => {
  const { t } = useTranslation();
  const spanSec = segments[segments.length - 1]?.toSec ?? 0;
  const points = breathPoints(segments, scores, spanSec);
  const measured = points.filter(
    (point): point is typeof point & { y: number } => point.y !== null,
  );

  if (measured.length === 0) {
    return null;
  }

  const toView = (point: { x: number; y: number | null }) => ({
    x: xOf(point.x),
    y: point.y === null ? null : yOf(point.y),
  });
  const scaled = points.map(toView);
  const curve = breathCurve(series, spanSec).map(toView);
  // 곡선이 한 칸이라도 있으면 그걸 그린다. 짧거나 끊긴 테이크에서 곡선이 전부 비면 점 잇기로.
  const drawn = curve.some((p) => p.y !== null) ? curve : scaled;
  const line = linePath(drawn);
  // 면도 선처럼 빈 칸에서 끊는다 — 이어진 구간마다 다각형 하나.
  const base = yOf(100).toFixed(1);
  const runs: { x: number; y: number }[][] = [];
  drawn.forEach((p, i) => {
    if (p.y === null) {
      return;
    }
    if (i === 0 || drawn[i - 1].y === null) {
      runs.push([]);
    }
    runs[runs.length - 1].push({ x: p.x, y: p.y });
  });
  const area = runs
    .map(
      (run) =>
        `M${run[0].x.toFixed(1)} ${base} ${run
          .map((p) => `L${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
          .join(' ')} L${run[run.length - 1].x.toFixed(1)} ${base} Z`,
    )
    .join(' ');

  /** 20~100 점수 → 뷰 y(0 = 위). `breathY`와 같은 자다. */
  const scoreY = (score: number): number => yOf((1 - (score - 20) / 80) * 100);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      role="img"
      aria-label={t('web.result.breath.chartLabel')}
    >
      {Y_TICKS.map((tick) => (
        <g key={tick}>
          <line
            x1={PAD.l}
            x2={W - PAD.r}
            y1={scoreY(tick)}
            y2={scoreY(tick)}
            stroke="var(--bk-100)"
            strokeWidth={1}
          />
          <text
            x={PAD.l - 10}
            y={scoreY(tick) + 4}
            textAnchor="end"
            fontSize={12}
            fill="var(--bk-600)"
          >
            {tick}
          </text>
        </g>
      ))}

      {average != null && (
        <line
          data-testid="breath-average"
          x1={PAD.l}
          x2={W - PAD.r}
          y1={scoreY(average)}
          y2={scoreY(average)}
          stroke="hsl(var(--primary) / 0.5)"
          strokeWidth={1}
          strokeDasharray="4 4"
        />
      )}

      <path
        data-testid="breath-area"
        d={area}
        fill="hsl(var(--primary) / 0.08)"
      />
      <path
        data-testid="breath-line"
        d={line}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {scaled.map(
        (point, i) =>
          point.y !== null && (
            <circle
              key={points[i].id}
              data-testid="breath-dot"
              cx={point.x}
              cy={point.y}
              r={4}
              fill="hsl(var(--primary))"
            />
          ),
      )}

      {/* 가로 눈금 — 구간 경계마다(0 · 3 · 7 · 10초 중 시안은 0·5·10을 찍지만 우리 경계는
          엔진이 소유한다). 첫 경계와 마지막 경계, 가운데 하나. */}
      {[0, spanSec / 2, spanSec].map((sec) => (
        <text
          key={sec}
          x={xOf((sec / spanSec) * 100)}
          y={H - 8}
          textAnchor="middle"
          fontSize={12}
          fill="var(--bk-600)"
        >
          {t('web.result.breath.axisSec', { value: Math.round(sec) })}
        </text>
      ))}
    </svg>
  );
};
